import { CategoryItem, GenericCarouselTargetType, GenreTray, StudiosTray } from '../../../domain/generic-carousel';
import {
  CategoryApiModel,
  CategoryTargetTypeApiModel,
  CreateCategoryApiModel,
  CreateGenrePageItemApiModel,
  CreateStudioPageItemApiModel,
  GenericLayoutApiModel,
  GenrePageItemApiModel,
  StudioPageItemApiModel,
} from '@cxb/editor-stb-api';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import {
  mapLocalizedDomainImagesToCreateLocalizedPlatformImageRequestApiModelArray,
  platformTargetingMapper,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import { StudiosTrayLayout } from '../../../domain/domain-layouts';
import { StudioPageItemLayoutApiModel } from '../../../shared/mock-models/mock-models';
import {
  commonMapper,
  mapApiModelImageToDomain,
  mapApiModelImageWithPlatformToDomain,
  scheduledDateMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { PlatformImageApiModel } from '@cxb/editor-stb-api/model/models';
import { Genre } from '../../../domain/genre';
import {
  CategoryViewModel,
  GenericTrayFormViewModel,
  GenreTrayFormViewModel,
  StudiosTrayFormValue,
  TargetType,
} from './generic-tray-form.view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  mapDomainImageToViewModelImage,
  mapLocalizedDomainImagesToPlatformLocalizedViewModelImage,
  mapSchedulingDomainToViewModel,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import { TraySelectorType } from '../tray-selector/tray-selector.enum';
import { LocalizedDomainImage } from '../../../domain/localized-domain-images';
import { LocalizedBackgroundImageViewModel } from '../../../shared/components/image-upload-control/image-upload-view-model';
import {
  mapViewModelImagesToLocalizedDomainImages,
  mapViewModelImageToDomainImageWithoutPlatforms,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';

// 'GenreTray'
// 'StudiosTray'

/**
 * DOMAIN -> API
 */
const mapDomainCategoryTargetTypeToApi = (targetType: GenericCarouselTargetType): CategoryTargetTypeApiModel => {
  switch (targetType) {
    case GenericCarouselTargetType.ContentGroup:
      return CategoryTargetTypeApiModel.Contentgroup;
    case GenericCarouselTargetType.Page:
      return CategoryTargetTypeApiModel.Page;
    default:
      return undefined;
  }
};

const mapDomainCategoryItemsToApi = (items: CategoryItem[]): CreateCategoryApiModel[] => {
  return items.map<CreateCategoryApiModel>((item, index) => ({
    feedId: item.feedId,
    images: mapLocalizedDomainImagesToCreateLocalizedPlatformImageRequestApiModelArray(item.backgroundImages),
    targetType: mapDomainCategoryTargetTypeToApi(item.targetType),
    titleLocalized: item.title,
    order: index,
    cotvMetaData: {
      marketingLabel: item.marketingLabel,
      tags: item.genres,
      keyart: [
        { imageId: item.keyart.en.id, locale: 'en' },
        { imageId: item.keyart.ar.id, locale: 'ar' },
      ],
      titleTreatment: [
        { imageId: item.titleTreatment.en.id, locale: 'en' },
        { imageId: item.titleTreatment.ar.id, locale: 'ar' },
      ],
    },
    internalName: item.title.en,
    pageId: item.pageId,
  }));
};

const domainStudioLayoutToApi: Record<StudiosTrayLayout, StudioPageItemLayoutApiModel> = {
  [StudiosTrayLayout.Small]: StudioPageItemLayoutApiModel.Small,
  [StudiosTrayLayout.Medium]: StudioPageItemLayoutApiModel.Medium,
  [StudiosTrayLayout.Large]: StudioPageItemLayoutApiModel.Large,
};

export const mapDomainGenreTrayToApi = (genreTray: GenreTray, pageVersionId: string, tenantId?: string): CreateGenrePageItemApiModel => {
  const commonProperties = mapperPipe(genreTray, targetingMapperDomainToApi, scheduledDateMapperDomainToApi, platformTargetingMapper);
  return remainderMapper(commonProperties, () => ({
    titleLocalized: genreTray.title,
    pageVersionId,
    categories: mapDomainCategoryItemsToApi(genreTray.categories),
    displayedOnTopOfPage: false,
    tenantId,
  }));
};

export const mapDomainStudiosTrayToApi = (
  studiosTray: StudiosTray,
  pageVersionId: string,
  tenantId?: string
): CreateStudioPageItemApiModel => {
  const commonProperties = mapperPipe(studiosTray, targetingMapperDomainToApi, scheduledDateMapperDomainToApi, platformTargetingMapper);

  return remainderMapper(commonProperties, () => ({
    nameLocalized: studiosTray.title,
    pageVersionId,
    categories: mapDomainCategoryItemsToApi(studiosTray.categories),
    displayedOnTopOfPage: false,
    layout: domainStudioLayoutToApi[studiosTray.layout] as unknown as GenericLayoutApiModel,
    tenantId,
  }));
};

/**
 * API -> DOMAIN
 */
const mapApiCategoryItemsToDomain = (items: CategoryApiModel[]): CategoryItem[] => {
  return items.map((item) => ({
    id: item.id,
    title: item.titleLocalized,
    targetType: undefined,
    pageId: item.pageId,
    feedId: item.feedId,
    backgroundImages: {
      en: item.images.en.map((i: PlatformImageApiModel) => mapApiModelImageWithPlatformToDomain(i)),
      ar: item.images.ar.map((i: PlatformImageApiModel) => mapApiModelImageWithPlatformToDomain(i)),
    },
    keyart: {
      en: mapApiModelImageToDomain(item.cotvMetaData.keyart.find((i) => i.language === 'en')),
      ar: mapApiModelImageToDomain(item.cotvMetaData.keyart.find((i) => i.language === 'ar')),
    },
    titleTreatment: {
      en: mapApiModelImageToDomain(item.cotvMetaData.titleTreatment.find((i) => i.language === 'en')),
      ar: mapApiModelImageToDomain(item.cotvMetaData.titleTreatment.find((i) => i.language === 'ar')),
    },
    marketingLabel: item.cotvMetaData.marketingLabel,
    genres: item.cotvMetaData.tags,
  }));
};

const mapApiStudiosLayoutToDomain = (template: GenericLayoutApiModel): StudiosTrayLayout => {
  switch (template) {
    case GenericLayoutApiModel.Small:
      return StudiosTrayLayout.Small;
    case GenericLayoutApiModel.Medium:
      return StudiosTrayLayout.Medium;
    case GenericLayoutApiModel.Large:
      return StudiosTrayLayout.Large;
  }
};

export const mapApiGenreTrayToDomain = (genreTray: GenrePageItemApiModel): GenreTray => {
  return remainderMapper(mapperPipe(genreTray, commonMapper, targetingMapperDomainToApi, scheduledDateMapperApiToDomain), () => ({
    title: genreTray.titleLocalized,
    type: 'GenreTray',
    categories: mapApiCategoryItemsToDomain(genreTray.categories),
  }));
};

export const mapApiStudiosTrayToDomain = (studiosTray: StudioPageItemApiModel): StudiosTray => {
  return remainderMapper(mapperPipe(studiosTray, commonMapper, targetingMapperDomainToApi, scheduledDateMapperApiToDomain), () => ({
    title: studiosTray.titleLocalized,
    type: 'StudiosTray',
    categories: mapApiCategoryItemsToDomain(studiosTray.categories),
    layout: mapApiStudiosLayoutToDomain(studiosTray.layout),
  }));
};

/**
 * DOMAIN -> VIEWMODEL
 */

const mapDomainGenericCarouselLayoutToViewModelGenericCarouselTemplate = (layout: StudiosTrayLayout): TraySelectorType => {
  switch (layout) {
    case StudiosTrayLayout.Small:
      return TraySelectorType.StudiosSmall;
    case StudiosTrayLayout.Medium:
      return TraySelectorType.StudiosMedium;
    case StudiosTrayLayout.Large:
      return TraySelectorType.StudiosLarge;
    default:
      return undefined;
  }
};

const mapLocalizedDomainImageToLocalizedViewModelImage = (image: LocalizedDomainImage): LocalizedBackgroundImageViewModel => {
  return {
    en: mapDomainImageToViewModelImage(image.en),
    ar: image.ar ? mapDomainImageToViewModelImage(image.ar) : undefined,
  };
};

const mapGenresApiStringArrayToGenres = (selectedGenres: string[], genres: Genre[]): Genre[] => {
  return genres.filter((g) => selectedGenres?.includes(g.id));
};

export const mapDomainGenreTrayItemToViewModelCategoryDetails = (categories: CategoryItem[], genres: Genre[]): CategoryViewModel[] => {
  return categories.map((item) => ({
    id: item.id,
    name: item.title,
    contentTarget: {
      targetType: (() => {
        if (item.pageId) {
          return TargetType.Page;
        } else if (item.feedId) {
          return TargetType.ContentGroup;
        }
        return undefined;
      })(),
      target: item.pageId || item.feedId,
    },
    images: {
      ...mapLocalizedDomainImagesToPlatformLocalizedViewModelImage(item.backgroundImages),
      'BIGSCREEN  - Keyart': mapLocalizedDomainImageToLocalizedViewModelImage(item.keyart),
      'BIGSCREEN  - Title treatment': mapLocalizedDomainImageToLocalizedViewModelImage(item.titleTreatment),
    },
    marketingLabel: item.marketingLabel,
    genres: mapGenresApiStringArrayToGenres(item.genres, genres),
  }));
};

export const mapDomainGenreTrayToViewModel = (genreTray: GenreTray, genres: Genre[]): GenericTrayFormViewModel => {
  return {
    id: genreTray.id,
    title: genreTray.title,
    userSegmentIds: genreTray.userSegmentIds,
    countryIds: genreTray.countryIds,
    scheduling: mapSchedulingDomainToViewModel(genreTray),
    categoryDetails: mapDomainGenreTrayItemToViewModelCategoryDetails(genreTray.categories, genres),
    type: TrayFormViewModelType.GenreTray,
    platformIds: genreTray.platformIds,
  };
};

export const mapDomainStudiosTrayToViewModel = (studiosTray: StudiosTray, genres: Genre[]): GenericTrayFormViewModel => {
  return {
    id: studiosTray.id,
    title: studiosTray.title,
    userSegmentIds: studiosTray.userSegmentIds,
    countryIds: studiosTray.countryIds,
    scheduling: mapSchedulingDomainToViewModel(studiosTray),
    categoryDetails: mapDomainGenreTrayItemToViewModelCategoryDetails(studiosTray.categories, genres),
    type: TrayFormViewModelType.StudiosTray,
    layout: mapDomainGenericCarouselLayoutToViewModelGenericCarouselTemplate(studiosTray.layout),
    platformIds: studiosTray.platformIds,
  };
};

/**
 * VIEWMODEL -> DOMAIN
 */

const mapStudiosTraySelectorTypeToDomain = (type: TraySelectorType): StudiosTrayLayout => {
  switch (type) {
    case TraySelectorType.StudiosSmall:
      return StudiosTrayLayout.Small;
    case TraySelectorType.StudiosMedium:
      return StudiosTrayLayout.Medium;
    case TraySelectorType.StudiosLarge:
      return StudiosTrayLayout.Large;
  }
};

const mapViewModelImageToLocalizedImageWithoutPlatforms = (
  image: LocalizedBackgroundImageViewModel,
  order?: number
): LocalizedDomainImage => {
  return {
    en: mapViewModelImageToDomainImageWithoutPlatforms(image.en),
    ar: image.ar ? mapViewModelImageToDomainImageWithoutPlatforms(image.ar) : undefined,
    order,
  };
};

const castCategoryDetailGenresToStringArray = (categoryDetailGenres: string[] | Genre[]): string[] => {
  let ret = [];
  if (Array.isArray(categoryDetailGenres) && categoryDetailGenres.length > 0) {
    if (typeof categoryDetailGenres[0] === 'string') {
      ret = categoryDetailGenres as string[];
    }
    if ((categoryDetailGenres[0] as Genre).id !== undefined) {
      ret = categoryDetailGenres.map((c) => c.id);
    }
  }
  return ret;
};

export const mapViewModelCategoryDetailsToDomain = (
  categoryDetails: CategoryViewModel[],
  platformIds: Record<PlatformType, string>
): CategoryItem[] => {
  return categoryDetails.map((categoryDetail) => {
    const { targetType, target } = categoryDetail.contentTarget;
    const item: CategoryItem = {
      id: categoryDetail.id,
      title: categoryDetail.name,
      marketingLabel: categoryDetail.marketingLabel,
      targetType: (() => {
        switch (targetType) {
          case TargetType.ContentGroup:
            return GenericCarouselTargetType.ContentGroup;
          case TargetType.Page:
            return GenericCarouselTargetType.Page;
          default:
            return undefined;
        }
      })(),
      pageId: targetType === TargetType.Page ? target : undefined,
      feedId: targetType === TargetType.ContentGroup ? target : undefined,
      backgroundImages: mapViewModelImagesToLocalizedDomainImages(categoryDetail.images, platformIds),
      keyart: mapViewModelImageToLocalizedImageWithoutPlatforms(categoryDetail.images['BIGSCREEN  - Keyart']),
      titleTreatment: mapViewModelImageToLocalizedImageWithoutPlatforms(categoryDetail.images['BIGSCREEN  - Title treatment']),
      genres: castCategoryDetailGenresToStringArray(categoryDetail.genres), // AXB-914
    };
    return item;
  });
};

export const mapViewModelGenreTrayToDomain = (genreTray: GenreTrayFormViewModel, platformIds: Record<PlatformType, string>): GenreTray => {
  return {
    id: genreTray.id,
    type: 'GenreTray',
    order: undefined,
    visible: undefined,
    draggable: true,
    title: genreTray.title,
    userSegmentIds: genreTray.userSegmentIds,
    scheduledFrom: genreTray.scheduling?.scheduledFrom ? new Date(genreTray.scheduling.scheduledFrom).getTime() : undefined,
    scheduledTo: genreTray.scheduling?.scheduledTo ? new Date(genreTray.scheduling.scheduledTo).getTime() : undefined,
    categories: mapViewModelCategoryDetailsToDomain(genreTray.categoryDetails, platformIds),
    countryIds: genreTray.countryIds,
    platformIds: genreTray.platformIds,
  };
};

export const mapViewModelStudiosTrayToDomain = (
  studioTray: StudiosTrayFormValue,
  platformIds: Record<PlatformType, string>
): StudiosTray => {
  return {
    id: studioTray.id,
    type: 'StudiosTray',
    order: undefined,
    visible: undefined,
    draggable: true,
    title: studioTray.title,
    layout: mapStudiosTraySelectorTypeToDomain(studioTray.layout),
    userSegmentIds: studioTray.userSegmentIds,
    scheduledFrom: studioTray.scheduling?.scheduledFrom ? new Date(studioTray.scheduling.scheduledFrom).getTime() : undefined,
    scheduledTo: studioTray.scheduling?.scheduledTo ? new Date(studioTray.scheduling.scheduledTo).getTime() : undefined,
    categories: mapViewModelCategoryDetailsToDomain(studioTray.categoryDetails, platformIds),
    countryIds: studioTray.countryIds,
    platformIds: studioTray.platformIds,
  };
};
